<template>
  <div class="detail-root">
    <header class="detail-header">
      <button class="back-btn" @click="router.push('/circulars')">
        <i class="pi pi-arrow-left"></i> Back
      </button>
    </header>

    <div v-if="loading" class="detail-skeleton">
      <ONESkeleton height="2rem" class="mb-3" />
      <ONESkeleton height="1rem" width="40%" class="mb-4" />
      <ONESkeleton height="300px" />
    </div>

    <div v-else-if="!item" class="empty-state">
      <i class="pi pi-inbox text-4xl text-slate-300"></i>
      <p class="text-slate-500 mt-2">Circular not found</p>
    </div>

    <div v-else class="detail-content">
      <div class="meta-row">
        <span class="type-badge" :class="item.CircularType === 1 ? 'type-org' : 'type-dept'">
          {{ item.CircularType === 1 ? 'Organisation' : 'Department' }}
        </span>
        <span v-if="!item.IsActive" class="inactive-badge">Inactive</span>
        <span v-if="item.DepartmentName" class="meta-dept">
          <i class="pi pi-building"></i> {{ item.DepartmentName }}
        </span>
        <span class="meta-date">
          <i class="pi pi-calendar"></i> {{ formatDate(item.PublishedDate || item.CreatedDate) }}
        </span>
        <span v-if="item.CreatedByName" class="meta-author">
          <i class="pi pi-user"></i> {{ item.CreatedByName }}
        </span>
        <span v-if="readTime > 0" class="meta-readtime">
          <i class="pi pi-clock"></i> {{ readTime }} min read
        </span>
      </div>

      <h1 class="detail-title">{{ item.Title }}</h1>

      <div v-if="item.ContentHtml" class="html-body ql-editor" v-html="item.ContentHtml"></div>
      <p v-else-if="item.Content" class="plain-body">{{ item.Content }}</p>

      <div v-if="item.FullAttachmentPath || item.AttachmentPath" class="attachment-section">
        <a :href="item.FullAttachmentPath || item.AttachmentPath" target="_blank" class="attachment-link">
          <i class="pi pi-paperclip"></i> Download Attachment
        </a>
      </div>

      <div v-if="canManage" class="admin-actions">
        <button class="action-btn edit-btn" @click="router.push('/circulars/' + item.Id + '/edit')">
          <i class="pi pi-pencil"></i> Edit
        </button>
        <button
          class="action-btn toggle-btn"
          :class="item.IsActive ? 'active-on' : 'active-off'"
          @click="toggleActive"
        >
          <i :class="item.IsActive ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
          {{ item.IsActive ? 'Deactivate' : 'Activate' }}
        </button>
        <button class="action-btn delete-btn" @click="showDeleteConfirm = true">
          <i class="pi pi-trash"></i> Delete
        </button>
      </div>

      <!-- Delete Confirm -->
      <div v-if="showDeleteConfirm" class="confirm-overlay" @click.self="showDeleteConfirm = false">
        <div class="confirm-dialog">
          <div class="confirm-icon"><i class="pi pi-exclamation-triangle"></i></div>
          <h3 class="confirm-title">Delete Circular?</h3>
          <p class="confirm-body">"<strong>{{ item.Title }}</strong>" will be permanently removed.</p>
          <div class="confirm-actions">
            <button class="conf-cancel-btn" @click="showDeleteConfirm = false">Cancel</button>
            <button class="conf-delete-btn" :disabled="deleting" @click="doDelete">
              <i v-if="deleting" class="pi pi-spin pi-spinner"></i>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import moment from 'moment'
import { getCircularById, activateCircular, deleteCircular, getDashboardUserContext } from '../../services/dashboardService'

const router = useRouter()
const route = useRoute()

const loading = ref(true)
const item = ref<any>(null)
const showDeleteConfirm = ref(false)
const deleting = ref(false)

const ctx = getDashboardUserContext()
const canManage = ctx.canManageSOP

const readTime = computed(() => {
  const text = (item.value?.ContentHtml?.replace(/<[^>]+>/g, '') ?? '') + (item.value?.Content ?? '')
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.ceil(words / 200))
})

function formatDate(d: any) {
  if (!d) return ''
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD MMM YYYY') : moment(d).format('DD MMM YYYY')
}

async function toggleActive() {
  if (!item.value) return
  const newStatus = !item.value.IsActive
  const ok = await activateCircular(item.value.Id, newStatus, ctx.userId)
  if (ok) item.value.IsActive = newStatus
}

async function doDelete() {
  if (!item.value) return
  deleting.value = true
  const ok = await deleteCircular(item.value.Id, ctx.userId)
  deleting.value = false
  if (ok) router.push('/circulars')
}

onMounted(async () => {
  const id = Number(route.params.id)
  if (id) item.value = await getCircularById(id)
  loading.value = false
})
</script>

<style scoped>
.detail-root { min-height: 100vh; background: var(--one-rule-soft); padding: 1.5rem; font-family: 'Inter', sans-serif; }
.detail-header { margin-bottom: 1rem; }
.back-btn { display: inline-flex; align-items: center; gap: 6px; background: none; border: 1px solid var(--one-rule); border-radius: 8px; padding: 6px 14px; color: var(--one-muted); font-size: 0.85rem; cursor: pointer; transition: all 0.15s; }
.back-btn:hover { background: #f8fafc; color: #0d9488; border-color: #5eead4; }
.detail-skeleton { max-width: 860px; margin: 0 auto; }
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem; text-align: center; }
.detail-content { max-width: 860px; margin: 0 auto; background: #fff; border: 1px solid var(--one-rule); border-radius: 12px; padding: 2rem; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }

.meta-row { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; margin-bottom: 1rem; font-size: 0.82rem; color: var(--one-muted); }
.type-badge { border-radius: 12px; padding: 3px 12px; font-size: 0.72rem; font-weight: 600; }
.type-org { background: #dbeafe; color: #1d4ed8; }
.type-dept { background: #d1fae5; color: #065f46; }
.inactive-badge { background: #fef3c7; color: #d97706; border-radius: 10px; padding: 2px 8px; font-size: 0.72rem; font-weight: 600; }
.meta-dept, .meta-date, .meta-author, .meta-readtime { display: inline-flex; align-items: center; gap: 4px; }
.detail-title { font-size: 1.75rem; font-weight: 700; color: #0f172a; margin: 0 0 1.25rem 0; line-height: 1.3; }
.html-body { font-size: 0.95rem; line-height: 1.75; color: #334155; padding: 0; }
.plain-body { font-size: 0.95rem; line-height: 1.75; color: #334155; white-space: pre-wrap; }
.attachment-section { border-top: 1px solid var(--one-rule-soft); padding-top: 1rem; margin-top: 1.5rem; }
.attachment-link { display: inline-flex; align-items: center; gap: 6px; color: #0d9488; text-decoration: none; padding: 6px 14px; border: 1px solid #5eead4; border-radius: 8px; background: #f0fdfa; font-size: 0.85rem; transition: all 0.15s; }
.attachment-link:hover { background: #ccfbf1; }

.admin-actions { border-top: 1px solid var(--one-rule-soft); padding-top: 1rem; margin-top: 1.5rem; display: flex; gap: 0.5rem; flex-wrap: wrap; }
.action-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: 8px; font-size: 0.85rem; cursor: pointer; border: 1px solid; transition: all 0.15s; }
.edit-btn { background: #eff6ff; color: #2563eb; border-color: #93c5fd; }
.edit-btn:hover { background: #dbeafe; }
.toggle-btn.active-on { background: #fef3c7; color: #d97706; border-color: #fcd34d; }
.toggle-btn.active-on:hover { background: #fef3c7; }
.toggle-btn.active-off { background: #f0fdf4; color: #16a34a; border-color: #86efac; }
.toggle-btn.active-off:hover { background: #dcfce7; }
.delete-btn { background: #fef2f2; color: #dc2626; border-color: #fca5a5; }
.delete-btn:hover { background: #fee2e2; }

.confirm-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; z-index: 1100; }
.confirm-dialog { background: #fff; border-radius: 14px; padding: 2rem; max-width: 420px; width: 90%; box-shadow: 0 20px 50px rgba(0,0,0,0.2); text-align: center; }
.confirm-icon { font-size: 2.5rem; color: #f59e0b; margin-bottom: 0.75rem; }
.confirm-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; margin: 0 0 0.5rem 0; }
.confirm-body { font-size: 0.875rem; color: var(--one-muted); line-height: 1.6; margin-bottom: 1.5rem; }
.confirm-actions { display: flex; gap: 0.75rem; justify-content: center; }
.conf-cancel-btn { padding: 8px 24px; border: 1px solid var(--one-rule); border-radius: 8px; background: #f8fafc; color: var(--one-muted); font-size: 0.875rem; cursor: pointer; }
.conf-cancel-btn:hover { background: var(--one-rule-soft); }
.conf-delete-btn { padding: 8px 24px; border: none; border-radius: 8px; background: #dc2626; color: #fff; font-size: 0.875rem; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.conf-delete-btn:hover:not(:disabled) { background: #b91c1c; }
.conf-delete-btn:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
